import * as React from "react";
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { CgDanger } from "react-icons/cg";
import { filterBy, FilterDescriptor } from "@progress/kendo-data-query";
import { DRAWER_WIDTH, INSERT_ACCESS } from "../../_contstants/common";
import { useAppDispatch } from "../../app/hooks";
import { checkAcessRights } from "../../_helper/helper";
import { opened } from "../drawer/drawerSlice";
import IconComponent from "../common/IconComponent";

const delay = 300;

const FormSingleSuggestionField = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    value,
    onChange,
    loading,
    fetchIncrementalData,
    options = [],
    isAddNew,
    drawerName,
    addNewLink,
    textField = "label",
    wrapperStyle,
    wrapperClassName,
    updateOptions,
    name,
    ref,
    astrike = false,
    ...others
  } = fieldRenderProps;
  const showValidationMessage: string | false | null =
    touched && validationMessage;
  const [isOpen, setIsOpen] = React.useState(false);
  const showHint: boolean = !showValidationMessage && hint;
  const hintId: string = showHint ? `${id}_hint` : "";
  const errorId: string = showValidationMessage ? `${id}_error` : "";
  const labelId: string = label ? `${id}_label` : "";
  const dispatch = useAppDispatch();

  const [data, setData] = React.useState(options);
  const [loading1, setLoading] = React.useState(loading);
  const [inputValue, setInputValue] = React.useState("");
  const timeout = React.useRef<NodeJS.Timeout | null>(null);

  const path = drawerName
    ? drawerName.replace(/form/i, "")
    : addNewLink
      ? addNewLink.replace(/\/create/i, "")
      : "";
  const isAddAccess = checkAcessRights(`/${path}`, INSERT_ACCESS);

  React.useEffect(() => {
    let updatedData = options;
    if (isAddNew && isAddAccess) {
      updatedData = [...updatedData, { label: "Add New", value: "addnew" }];
    }
    setData(updatedData);
  }, [options, isAddNew]);

  const filterData = (filter: FilterDescriptor) => {
    let updatedData = options;
    if (isAddNew && isAddAccess) {
      updatedData = [...updatedData, { label: "Add New", value: "addnew" }];
    }
    const data = updatedData?.slice();
    return filterBy(data, filter);
  };

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    setInputValue(event.filter?.value || "");

    timeout.current = setTimeout(() => {
      fetchIncrementalData(event.filter?.value || "");
      setData(filterData(event.filter));
      setLoading(false);
    }, delay);

    timeout.current = setTimeout(() => {
      if (!event.filter?.value) {
        onChange({ value: null });
        return;
      }
      const existingOption = data.find((item: any) => {
        item.label?.toLowerCase()?.includes(event.filter?.value?.toLowerCase());
      });
      if (!existingOption) {
        // Create new option
        const newOption = {
          label: `${event.filter?.value}` || inputValue,
          value: `${inputValue}`,
        };
        setData([...data, newOption]);
        if (typeof updateOptions === "function") {
          updateOptions(newOption);
        }

        onChange(newOption);
      }
    }, 500);
  };

  // Simple focus handler - just like MultiSelect
  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleSelect = (event: any) => {
    const selectedValue = event.target.value;
    if (!selectedValue) {
      onChange({ value: null });
      setIsOpen(false);
      return;
    }

    if (selectedValue?.value === "addnew") {
      setData(filterData({ field: "label", operator: "contains", value: "" }));
      if (addNewLink) {
        window.open(`${location.origin}/${addNewLink}`);
      } else {
        dispatch(
          opened({
            open: true,
            anchor: "right",
            name: drawerName,
            drawerWidth: DRAWER_WIDTH,
          }),
        );
      }
      setIsOpen(false);
      return;
    }

    const existingOption = data.find(
      (item: any) => item.value === selectedValue?.value,
    );
    if (selectedValue && !existingOption) {
      const newOption = {
        label: selectedValue.label || inputValue,
        value: inputValue,
      };
      setData([...data, newOption]);
      setIsOpen(false);
      if (typeof updateOptions === "function") {
        updateOptions(newOption);
      }
      onChange(newOption);
    } else {
      setIsOpen(false);
      onChange(selectedValue);
    }
  };

  let initialValue = options?.find((option: any) => option.value === value);

  return (
    <>
      <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
        <Label editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label} {astrike && <span style={{ color: "red" }}>*</span>}
        </Label>

        <div onClick={() => !disabled && setIsOpen(true)}>
          <ComboBox
            ariaLabelledBy={labelId}
            size={"large"}
            ariaDescribedBy={`${hintId} ${errorId}`}
            ref={ref}
            valid={valid}
            id={id}
            disabled={disabled}
            {...others}
            data={data}
            name={name}
            value={initialValue || null}
            filterable={true}
            onFilterChange={filterChange}
            loading={loading1}
            textField={textField}
            defaultValue={value}
            onFocus={handleFocus}
            onClose={() => setIsOpen(false)}
            opened={isOpen}
            onChange={handleSelect}
          />
        </div>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error className="d-flex justify-content-end" id={errorId}>
            <span
              className="d-flex align-items-center"
              style={{
                background: "#DF4957",
                color: "white",
                padding: "1px 8px 1px 5px",
                borderRadius: 10,
                gap: 5,
              }}
            >
              <IconComponent icon={CgDanger} /> {validationMessage}
            </span>
          </Error>
        )}
      </FieldWrapper>
    </>
  );
};

export default FormSingleSuggestionField;
